import ReactDatePicker from 'react-datepicker';

import { ClickAwayListener, Fade, Popper, styled } from '@mui/material';
import { usePopupContext } from '@kc-console/common-ui/singleton';

import { useCalendarProps } from '../hooks/useCalendarProps';
import { useClickAwayHandler } from '../hooks/useClickAwayHandler';
import { useDatePickerHandlers } from '../hooks/useDatePickerHandlers';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { datePickerClasses } from '../theme';
import { getLocale, validateCalendarProps } from '../utils';
import { usePickerConfig } from './PickerConfigContext';

import type { CalendarProps } from '../types';

import 'react-datepicker/dist/react-datepicker.css';

const DatePickerCalendar = (props: CalendarProps) => {
  const {
    anchorEl,
    open,
    onClose,
    locale = 'ko',
    monthsShown = 1,
    selectsRange = false,
    shouldCloseOnSelect = true,
    showTime = false,
    showSeconds = true,
    showTimezone = false,
    inline = false,
  } = props;
  const popupContext = usePopupContext();
  const pickerConfig = usePickerConfig();
  const calendarView = pickerConfig?.calendarView;
  const canonicalizeDate = pickerConfig?.canonicalizeDate;
  const disabled = pickerConfig?.disabled ?? false;

  // monthsShown과 showTime 호환성 검증
  const finalShowTime = calendarView ? false : validateCalendarProps(monthsShown, showTime);
  const dateLocale = getLocale(locale);

  // 날짜/시간 변경 핸들러
  const { handleSingleDateChange, handleRangeDateChange, handleTimeChange } = useDatePickerHandlers(
    {
      props,
      finalShowTime,
      shouldCloseOnSelect,
      onClose,
      canonicalizeDate,
    },
  );

  // Calendar props 생성
  const calendarProps = useCalendarProps({
    props,
    dateLocale,
    locale,
    finalShowTime,
    showSeconds,
    showTimezone,
    monthsShown,
    selectsRange,
    inline,
    handleTimeChange,
    calendarView,
    canonicalizeDate,
    disabled,
  });

  // ClickAway 핸들러
  const handleClickAway = useClickAwayHandler({
    anchorEl,
    onClose,
  });

  // ESC 키로 닫기 (inline이 아닐 때만)
  useEscapeKey({
    onEscape: onClose,
    enabled: open && !inline,
  });

  // inline 모드: Popper 없이 직접 렌더링
  // NOTE: 닫을 popper가 없으므로 shouldCloseOnSelect는 항상 false.
  // true이면 선택 시 react-datepicker가 setOpen(false)를 호출하면서 preSelection을
  // 아직 갱신되지 않은 props.selected(=이전 날짜)로 되돌리고, 키보드 선택 경로에는
  // 마우스 경로와 달리 이를 덮어쓰는 setPreSelection이 없어 포커스가 이전 날짜로 옮겨간다.
  if (inline) {
    return (
      <div role="region">
        {selectsRange ? (
          <ReactDatePicker
            {...calendarProps}
            selectsRange
            shouldCloseOnSelect={false}
            onChange={handleRangeDateChange}
          />
        ) : (
          <ReactDatePicker
            {...calendarProps}
            selectsRange={undefined}
            shouldCloseOnSelect={false}
            onChange={handleSingleDateChange}
          />
        )}
      </div>
    );
  }

  // 기본 모드: Popper로 렌더링
  return (
    <CalendarPopper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      transition
      role="dialog"
      ownerZIndex={popupContext?.zIndex}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={250}>
          <div role="document">
            <ClickAwayListener onClickAway={handleClickAway}>
              {selectsRange ? (
                <ReactDatePicker
                  {...calendarProps}
                  selectsRange
                  shouldCloseOnSelect={shouldCloseOnSelect}
                  onChange={handleRangeDateChange}
                />
              ) : (
                <ReactDatePicker
                  {...calendarProps}
                  selectsRange={undefined}
                  shouldCloseOnSelect={shouldCloseOnSelect}
                  onChange={handleSingleDateChange}
                />
              )}
            </ClickAwayListener>
          </div>
        </Fade>
      )}
    </CalendarPopper>
  );
};

DatePickerCalendar.displayName = 'Calendar';

export default DatePickerCalendar;

const CalendarPopper = styled(Popper, {
  target: datePickerClasses.popper,
  shouldForwardProp: prop => prop !== 'ownerZIndex',
})<{ ownerZIndex?: number }>`
  z-index: ${({ ownerZIndex, theme }) => ownerZIndex ?? theme.zIndex.contentPopup};
`;
